/**
 * pogi-qol.js — quality-of-life UX polish. PURELY ADDITIVE & non-blocking: nothing
 * here preventDefaults a navigation, a form submit, or an add to cart. Delete the
 * <script> tag in layout/theme.liquid to remove all of it cleanly.
 *
 * ES5-only (var / function exprs / index for-loops — no arrow/const/let/template
 * literals/optional chaining/NodeList.forEach). Feature-detected, idempotent,
 * reduced-motion + Save-Data aware. Hardened after a 4-lens adversarial audit
 * (functional / races / cross-browser-mobile / a11y) — see the worklog.
 *
 * Features:
 *   1. window.pogiAnnounce(msg) — a polite SR live region for transient button
 *      states. MODAL-AWARE: when a <dialog> is open (the cart drawer) it writes into
 *      a region INSIDE that dialog, because showModal() makes the rest of the page
 *      inert and a body-level live region would be swallowed.
 *   2. Optimistic drawer open — open the auto-open cart drawer the instant a valid
 *      add-to-cart CTA is clicked, instead of waiting for /cart/add.js to return.
 *   3. Haptic — a faint tick on a confirmed add (Android; iOS ignores vibrate). The
 *      header cart bubble already animates natively in Horizon, so we add no 2nd bump.
 *   4. Intent prefetch — prefetch a product/collection/page link on hover/touch.
 *   5. Image fade-in — lazy images that haven't loaded yet fade in instead of popping.
 */
(function () {
  'use strict';

  var doc = document;
  var REDUCE = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);

  // ===========================================================================
  // 1) Screen-reader announcer (modal-aware)
  // ===========================================================================
  function makeRegion() {
    var el = doc.createElement('div');
    el.className = 'visually-hidden';
    el.setAttribute('aria-live', 'polite');
    el.setAttribute('role', 'status');
    el.setAttribute('aria-atomic', 'true');
    return el;
  }
  function bodyRegion() {
    var r = doc.getElementById('pogi-sr');
    if (!r) {
      r = makeRegion();
      r.id = 'pogi-sr';
      (doc.body || doc.documentElement).appendChild(r);
    }
    return r;
  }
  function dialogRegion(dlg) {
    var r = dlg.querySelector('[data-pogi-sr]');
    if (!r) {
      r = makeRegion();
      r.setAttribute('data-pogi-sr', '1');
      dlg.appendChild(r);
    }
    return r;
  }
  var srTimer, srSetTimer;
  // Exposed so pogi-atc-feedback.js can announce 'Adding to cart'/'Checking out'/etc.
  window.pogiAnnounce = function (msg) {
    if (!msg) return;
    clearTimeout(srTimer);
    // Defer past the optimistic-open showModal() rAF (~16ms) so we can tell whether a
    // modal is open and target the NON-inert region. ~80ms is imperceptible for AT.
    srTimer = window.setTimeout(function () {
      try {
        // Target a live region inside the TOPMOST open modal (showModal makes the rest
        // of the page inert, so a body region would be swallowed). Reuse the drawer's
        // pre-existing ref="liveRegion" when present — more reliable than a region we
        // insert and populate dynamically — else an in-dialog region, else the body one.
        var modals = doc.querySelectorAll('dialog[open]');
        var modal = modals.length ? modals[modals.length - 1] : null;
        var target = modal ? (modal.querySelector('[ref="liveRegion"]') || dialogRegion(modal)) : bodyRegion();
        target.textContent = ''; // clear first so an identical repeat re-announces
        clearTimeout(srSetTimer);
        srSetTimer = window.setTimeout(function () { target.textContent = String(msg); }, 30);
      } catch (e) { /* never let an announcement break anything */ }
    }, 80);
  };

  // ===========================================================================
  // 2) Optimistic drawer open  +  3) haptic
  //    showModal() is DEFERRED to a rAF in Horizon (dialog.js), so dialog.open lags a
  //    frame — `pogiOpening` (a synchronous flag) is the real re-entry lock. We do NOT
  //    run a complex correlate-by-product state machine: open showing the real cart,
  //    clear the loading cue on the add's cart:update, and only close on a definitive
  //    failure (or the stranded-no-event fallback). pogiOpening is cleared on the FIRST
  //    cart:update of any kind, so the close-on-failure window stays tiny.
  // ===========================================================================
  var pogiOpening = false;
  var cueTimer;
  var CUE_TIMEOUT_MS = 2500; // drop the loading cue if the add's event hasn't arrived by now

  function autoOpenDrawer() {
    var d = doc.querySelector('cart-drawer-component[auto-open]');
    return d && typeof d.open === 'function' ? d : null;
  }
  function drawerDialog(d) {
    return (d.refs && d.refs.dialog) || d.querySelector('dialog');
  }
  function setLoading(d, on) {
    if (!d) return;
    var dlg = drawerDialog(d);
    if (!dlg) return;
    if (dlg.classList) dlg.classList.toggle('pogi-drawer-loading', !!on); // visual ::after bar
    // aria-busy goes on the INNER content node, NOT the dialog: the dialog hosts the
    // polite live regions (the drawer's ref="liveRegion" + our [data-pogi-sr]), and
    // aria-busy on an ancestor buffers/suppresses their announcements until it clears
    // (ARIA spec). .cart-drawer__inner is a sibling of those regions, so they stay free.
    var inner = dlg.querySelector('.cart-drawer__inner');
    if (inner) {
      if (on) inner.setAttribute('aria-busy', 'true');
      else inner.removeAttribute('aria-busy');
    }
  }
  function haptic() {
    if (navigator.vibrate) { try { navigator.vibrate(8); } catch (e) {} }
  }

  doc.addEventListener('click', function (e) {
    var node = e.target;
    if (node && node.nodeType === 3) node = node.parentNode;
    if (!node || typeof node.closest !== 'function') return;
    var btn = node.closest('[data-pogi-atc]');
    if (!btn) return;
    if (btn.disabled || btn.getAttribute('aria-disabled') === 'true') return;
    if (/sold\s*out/i.test(btn.textContent || '')) return;
    var drawer = autoOpenDrawer();
    if (!drawer) return;
    var dlg = drawerDialog(drawer);
    if (pogiOpening || (dlg && dlg.open)) return; // synchronous re-entry lock (+ already-open)
    pogiOpening = true;
    setLoading(drawer, true);
    try {
      drawer.open();
    } catch (err) {
      pogiOpening = false;
      setLoading(drawer, false);
      return;
    }
    clearTimeout(cueTimer);
    cueTimer = window.setTimeout(function () {
      // No add event yet — either a slow add still in flight, or a stranded client
      // max-qty bail / network drop that fires nothing. Drop the visual + AT loading cue
      // and release the re-entry lock, but DON'T auto-close: closing here would flicker a
      // slow-but-successful add (it reopens when it lands), and a stranded add just leaves
      // a normal open cart drawer (no spinner), consistent with auto-open-on-add. A
      // genuine server failure still closes via resolveOptimistic's didError path.
      if (pogiOpening) {
        setLoading(drawer, false);
        pogiOpening = false;
      }
    }, CUE_TIMEOUT_MS);
  });

  function resolveOptimistic(failed) {
    var drawer = autoOpenDrawer();
    clearTimeout(cueTimer);
    setLoading(drawer, false);
    if (failed && pogiOpening && drawer) {
      // Definitive failure (server 422 / sold out). We opened for THIS add and nothing
      // succeeded yet, so close — the PDP error stands alone. Only fires while
      // pogiOpening (a sub-network-latency window), so it can't close a drawer the user
      // has since filled. (close() is safe: the add's cart:update is ≥1 network RT after
      // click, well after the ~16ms open rAF.)
      try { drawer.close(); } catch (e) {}
    }
    pogiOpening = false;
  }

  doc.addEventListener('cart:update', function (e) {
    var d = e && e.detail && e.detail.data;
    var failed = !!(d && d.didError);
    var wasOurs = pogiOpening;
    resolveOptimistic(failed);
    if (failed) return;
    if (wasOurs) haptic(); // tick only on the add we optimistically opened for
  });

  // ===========================================================================
  // 4) Intent prefetch
  // ===========================================================================
  (function () {
    var conn = navigator.connection;
    if (conn && (conn.saveData || /(^|-)2g$/.test(conn.effectiveType || ''))) return; // data saver
    var done = {};
    var count = 0;
    var MAX = 12;
    var hoverTimer, touchTimer;

    function eligible(a) {
      if (!a || !a.href || a.target === '_blank' || a.hasAttribute('download')) return null;
      if (/external|nofollow/i.test(a.getAttribute('rel') || '')) return null;
      var href = a.href;
      if (href.indexOf(location.origin) !== 0) return null;                 // same-origin only
      if (href.split('#')[0] === location.href.split('#')[0]) return null;  // skip current page / #anchors
      // navigational content pages only, allowing an optional /xx or /xx-yy locale prefix
      if (!/^(\/[a-z]{2}(-[a-z]{2})?)?\/(products|collections|pages|blogs)\//i.test(a.pathname || '')) return null;
      if (done[href]) return null;
      return href;
    }
    function prefetch(href) {
      if (!href || done[href] || count >= MAX) return;
      done[href] = true;
      count++;
      var link = doc.createElement('link');
      link.rel = 'prefetch';
      link.href = href;
      link.as = 'document';
      (doc.head || doc.documentElement).appendChild(link);
    }
    function anchorFrom(e) {
      var node = e.target;
      if (node && node.nodeType === 3) node = node.parentNode;
      if (!node || typeof node.closest !== 'function') return null;
      return node.closest('a[href]');
    }
    // Mouse: hover-intent debounce.
    doc.addEventListener('mouseover', function (e) {
      var href = eligible(anchorFrom(e));
      if (!href) return;
      clearTimeout(hoverTimer);
      hoverTimer = window.setTimeout(function () { prefetch(href); }, 70);
    }, true);
    doc.addEventListener('mouseout', function () { clearTimeout(hoverTimer); }, true);
    // Keyboard focus: strong intent, go now.
    doc.addEventListener('focusin', function (e) {
      var href = eligible(anchorFrom(e));
      if (href) prefetch(href);
    }, true);
    // Touch: schedule, but cancel if the finger moves (a scroll, not a tap) — no
    // prefetching full pages from tap-and-scroll on mobile data.
    doc.addEventListener('touchstart', function (e) {
      var href = eligible(anchorFrom(e));
      if (!href) return;
      clearTimeout(touchTimer);
      touchTimer = window.setTimeout(function () { prefetch(href); }, 120);
    }, { capture: true, passive: true });
    doc.addEventListener('touchmove', function () { clearTimeout(touchTimer); }, { capture: true, passive: true });
    doc.addEventListener('scroll', function () { clearTimeout(touchTimer); }, { capture: true, passive: true });
  })();

  // ===========================================================================
  // 5) Image fade-in (lazy images that aren't loaded yet) — verified a11y/hidden-safe
  // ===========================================================================
  (function () {
    if (REDUCE) return;
    function arm(img) {
      if (!img || img.getAttribute('data-pogi-fade') === '1') return;
      if (img.complete) return;           // already painted — never hide it
      if (img.loading !== 'lazy') return; // only below-the-fold images
      img.setAttribute('data-pogi-fade', '1');
      img.classList.add('pogi-fade');
    }
    function show(img) {
      if (img && img.classList && img.classList.contains('pogi-fade')) img.classList.add('pogi-in');
    }
    function pass() {
      var imgs = doc.querySelectorAll('img[loading="lazy"]');
      for (var i = 0; i < imgs.length; i++) arm(imgs[i]);
    }
    // 'load' doesn't bubble; capture-phase on document still catches every image load.
    doc.addEventListener('load', function (e) {
      if (e.target && e.target.tagName === 'IMG') show(e.target);
    }, true);
    if (doc.readyState !== 'loading') pass();
    else doc.addEventListener('DOMContentLoaded', pass);
    // Watchdog: never leave a faded image invisible if its load/error event was missed.
    window.setTimeout(function () {
      var faded = doc.querySelectorAll('img.pogi-fade');
      for (var i = 0; i < faded.length; i++) show(faded[i]);
    }, 3000);
  })();
})();
